/**
 * DS2026 toast store — a tiny module-level observable so the imperative API
 * (`notifySuccess(...)`, the `react-toastify` shim) can push toasts from
 * anywhere, while the mounted `<Toaster>` subscribes via `useSyncExternalStore`.
 *
 * Spec + rationale: components/FbUI/DS2026-TOAST.md.
 */
import type { ReactNode } from 'react'

export type ToastVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'message'
  | 'loading'

export type ToastId = string | number

/** Props passed to render-function content (react-toastify compatibility). */
export type ToastRenderProps = { closeToast: () => void; data?: unknown }
/** Toast body — a static node, or a function rendered with `{ closeToast }`. */
export type ToastContent = ReactNode | ((props: ToastRenderProps) => ReactNode)

export type ToastData = {
  id: ToastId
  variant: ToastVariant
  title: ToastContent
  /** Optional inline text link (e.g. "View" to a created entity). */
  action?: { label: string; onClick?: () => void }
  /** ms until auto-dismiss; null = sticky (e.g. loading). undefined = default. */
  duration?: number | null
}

const DEFAULT_DURATION = 4000

let toasts: ToastData[] = []
let nextId = 1
let paused = false
const listeners = new Set<() => void>()
const timers = new Map<ToastId, ReturnType<typeof setTimeout>>()
// Wall-clock dismiss time per running timer — lets pause capture the *remaining*
// duration so resume continues from there instead of restarting the full clock.
const expiresAt = new Map<ToastId, number>()
// While paused, the ms each toast still owes (captured remaining, or a full
// duration for toasts added mid-pause); applied by resumeTimers().
const pendingMs = new Map<ToastId, number>()

// `toasts` is replaced with a new array on every change, so the reference is
// stable between changes — exactly what useSyncExternalStore's getSnapshot wants.
export const subscribe = (cb: () => void) => {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
export const getToasts = () => toasts

const emit = () => listeners.forEach((l) => l())

const fullMs = (duration: number | null | undefined): number | null =>
  duration === undefined ? DEFAULT_DURATION : duration

const stopTimer = (id: ToastId) => {
  const existing = timers.get(id)
  if (existing) clearTimeout(existing)
  timers.delete(id)
  expiresAt.delete(id)
}

// Start a real countdown of exactly `ms` (a concrete remaining/full duration).
const startTimer = (id: ToastId, ms: number) => {
  stopTimer(id)
  expiresAt.set(id, Date.now() + ms)
  timers.set(
    id,
    setTimeout(() => dismissToast(id), ms)
  )
}

// Arm a toast for its (initial or updated) duration. Sticky → never armed.
// While paused, the duration is remembered and applied on resume instead.
const arm = (id: ToastId, duration: number | null | undefined) => {
  stopTimer(id)
  pendingMs.delete(id)
  const ms = fullMs(duration)
  if (ms === null) return
  if (paused) pendingMs.set(id, ms)
  else startTimer(id, ms)
}

/**
 * Add a toast. `explicitId` mirrors react-toastify's `toastId`: if a toast with
 * that id is already active the call is **deduped** (ignored), so repeated calls
 * with a stable id can't stack duplicates.
 */
export const addToast = (
  input: Omit<ToastData, 'id'>,
  explicitId?: ToastId
): ToastId => {
  if (explicitId != null) {
    if (toasts.some((t) => t.id === explicitId)) return explicitId // dedupe
    toasts = [...toasts, { ...input, id: explicitId }]
    arm(explicitId, input.duration)
    emit()
    return explicitId
  }
  const id = nextId++
  toasts = [...toasts, { ...input, id }]
  arm(id, input.duration)
  emit()
  return id
}

export const updateToast = (
  id: ToastId,
  patch: Partial<Omit<ToastData, 'id'>>
): void => {
  toasts = toasts.map((t) => (t.id === id ? { ...t, ...patch } : t))
  if ('duration' in patch) arm(id, patch.duration)
  emit()
}

export const dismissToast = (id: ToastId): void => {
  toasts = toasts.filter((t) => t.id !== id)
  stopTimer(id)
  pendingMs.delete(id)
  // Emptying the stack while hovered strands `paused` (mouseleave never fires
  // when the hovered element is removed) — clear it so the next toast isn't
  // born paused and stuck. A genuine continued hover re-pauses via onMouseEnter.
  if (toasts.length === 0) {
    paused = false
    pendingMs.clear()
  }
  emit()
}

export const dismissAll = (): void => {
  timers.forEach((timer) => clearTimeout(timer))
  timers.clear()
  expiresAt.clear()
  pendingMs.clear()
  // No toasts means nothing can be hovered — never leave the store paused, or
  // the next toast lands in pendingMs and never auto-dismisses. (See dismissToast.)
  paused = false
  toasts = []
  emit()
}

export const isActive = (id: ToastId): boolean =>
  toasts.some((t) => t.id === id)

/**
 * Pause every auto-dismiss timer (while the stack is hovered/expanded) and
 * capture each toast's *remaining* time, so resume continues from there.
 */
export const pauseTimers = (): void => {
  if (paused) return
  paused = true
  const now = Date.now()
  timers.forEach((timer, id) => {
    clearTimeout(timer)
    const exp = expiresAt.get(id)
    if (exp != null) pendingMs.set(id, Math.max(0, exp - now))
  })
  timers.clear()
  expiresAt.clear()
}

/** Resume auto-dismiss, scheduling each toast for its captured remaining time. */
export const resumeTimers = (): void => {
  if (!paused) return
  paused = false
  toasts.forEach((t) => {
    const ms = pendingMs.has(t.id) ? pendingMs.get(t.id)! : fullMs(t.duration)
    if (ms !== null) startTimer(t.id, ms)
  })
  pendingMs.clear()
}
